import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Typography, CircularProgress, useMediaQuery, TextField } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { SidebarContext } from "src/contexts/SidebarContext";
import { updatespendLimitData } from "src/api/updateSpentLimitData";
import { showErrorToast } from "src/components/AlertToastify/AlertToastify";
import { useAuth0 } from "@auth0/auth0-react";
import { UpdatespendLimitInterface } from "src/types/UpdateSpentLimit";

function RequestsChart() {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:960px)");

  const { user, getAccessTokenSilently } = useAuth0();

  const { monthlyBill, isDataLoaded, clientInfo, setIsDataLoaded } = useContext(SidebarContext);

  //Sate for store client update spend limit
  const [updatespendLimit, setUpdatespendLimit] = useState<UpdatespendLimitInterface | null>(null);
  const [inputLimit, setInputLimit] = useState<number | "">("");

  const currentSpend = monthlyBill?.current_spend ?? 0;
  const monthlyLimit = monthlyBill?.monthly_limit ?? 0;
  // const usagePercentage = monthlyBill?.usage_percentage ?? 0;
  const newLimit = updatespendLimit?.new_limit || monthlyLimit;
  //Convert percentage from string to number
  const usagePercentage = Number(((currentSpend / newLimit) * 100).toFixed(2));

  const circleSize = 150;
  const circleSizeMobile = 120;
  const circleSizeTablet = 120;

  const size = isMobile ? circleSizeMobile : isTablet ? circleSizeTablet : circleSize;

  //Function for change value of new limits input
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    //Accept only numbers (no string or other character) whitout bar to increase and decrease amount in type number on the textarea
    if (/^\d*$/.test(value)) {
      setInputLimit(value === "" ? "" : Number(value));
    }
  };

  const handleIncreaseLimits = async () => {
    if (typeof inputLimit !== "number" || inputLimit <= 0) {
      showErrorToast("Please enter a valid limit greater than 0.");
      return;
    }

    try {
      const accessAuthToken = await getAccessTokenSilently();

      if (clientInfo?.clientId) {
        const spendLimitData = await updatespendLimitData(accessAuthToken, clientInfo.clientId, inputLimit);
        //Update monthlyBill state to render client monthly Bill data on the dashboard
        setUpdatespendLimit(spendLimitData);
        setIsDataLoaded(true);
      }
    } catch (error) {
      console.error("Error updating spend limit:", error);
      showErrorToast("Failed to update the spend limit. Please try again.");
    }
  };

  console.log("monthlyBill", monthlyBill);
  console.log("updatespendLimit", updatespendLimit);

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-around"
      // width={{ xs: "100%", lg: "48%" }}
      sx={{
        background: `${PureLightThemeCaaS.colors.alpha.grayBackgroundCard[100]}`,
        border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
        borderRadius: 0.5,
        padding: 1,
        minHeight: { lg: "275px", md: "235px", sm: "200px", xs: "175px" },
        height: "100%",
      }}
    >
      {isDataLoaded ? (
        <>
          <Box
            sx={{
              position: "relative",
              width: circleSize,
              height: circleSize,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Circular progress black */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={size}
              thickness={5}
              sx={{
                color: `${PureLightThemeCaaS.colors.alpha.black[50]}`,
              }}
            />

            {/* Circular progress orange */}
            <CircularProgress
              variant="determinate"
              value={usagePercentage}
              size={size}
              thickness={5}
              sx={{
                position: "absolute",
                color: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
              }}
            />
            <Typography
              variant="h4"
              fontFamily="Roboto, Arial"
              color={PureLightThemeCaaS.colors.alpha.white[100]}
              sx={{
                position: "absolute",
              }}
            >
              {`${usagePercentage}%`}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" alignItems="flex-end" justifyContent="space-between">
            <Box mb={2} sx={{ pl: { lg: 0, sm: 2 } }}>
              <Typography variant="h3" fontFamily="Roboto, Arial" color={PureLightThemeCaaS.colors.alpha.white[100]}>
                ${currentSpend.toFixed(2)}
              </Typography>
              <Typography variant="h6" fontFamily="Roboto, Arial" color={PureLightThemeCaaS.colors.alpha.white[100]}>
                / ${updatespendLimit?.new_limit ? Number(updatespendLimit.new_limit).toFixed(2) : monthlyLimit.toFixed(2)}
                limit
              </Typography>

              <Box pt={1} textAlign="left">
                <TextField
                  label="New limit"
                  type="text"
                  variant="outlined"
                  size="small"
                  color="warning"
                  autoComplete="off"
                  value={inputLimit}
                  onChange={handleInputChange}
                  InputProps={{
                    sx: {
                      fontFamily: "Roboto, arial",
                      textAlign: "center",
                      color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                      "&.Mui-focused": {
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                      },
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Roboto, arial",
                      color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                      lineHeight: 1.5,
                    },
                  }}
                  sx={{
                    width: "100px",
                    height: "auto",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                      },
                    },
                    fontFamily: "Roboto, arial",
                    "& .MuiInputBase-input": {
                      color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    },
                    "& .MuiInputLabel-root": {
                      color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    },
                  }}
                />
                {/* <Box display="flex" justifyContent="center"> */}
                <Box p={0} pt={1}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleIncreaseLimits}
                    color="primary"
                    sx={{ fontFamily: "Roboto, arial", fontSize: { lg: "12px", xs: "10px" } }}
                  >
                    INCREASE LIMITS
                  </Button>
                </Box>
                {/* </Box> */}
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              position: "relative",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              bgcolor: PureLightThemeCaaS.colors.alpha.grayBackgroundCard[100],
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress size={64} disableShrink thickness={3} sx={{ color: PureLightThemeCaaS.colors.alpha.orange[100] }} />
          </Box>
        </>
      )}
    </Box>
  );
}

export default RequestsChart;
